import React, { useState } from 'react';
import { fmtKm } from '../utils/distance';
import { useVisited } from '../context/VisitedContext';
import './TempleCard.css';
import './ShareCard.css';

const FAITH_LABELS = {
  hindu:    { en: 'Hindu',    hi: 'हिंदू' },
  buddhist: { en: 'Buddhist', hi: 'बौद्ध' },
  jain:     { en: 'Jain',     hi: 'जैन'  },
};

export default function TempleCard({ temple, lang, defaultOpen = false, distance = null }) {
  const [open, setOpen]           = useState(defaultOpen || (!!temple.id && window.location.hash === '#' + temple.id));
  const [storyOpen, setStoryOpen] = useState(false);
  const [imgError, setImgError]   = useState(false);
  const [copied, setCopied]       = useState(false);

  const { visited, toggle, notes, setNote } = useVisited();
  const isVisited = temple.id ? visited.has(temple.id) : false;
  const hasNote   = temple.id ? !!notes[temple.id] : false;

  const L = (en, hi) => lang === 'en' ? en : hi;
  const hasImg = temple.image_url && !imgError;
  const name  = L(temple.name_en,  temple.name_hi);
  const sub   = L(temple.name_hi,  temple.name_en);
  const deity = L(temple.deity_en, temple.deity_hi);
  const story = L(temple.story_en, temple.story_hi);
  const sig   = L(temple.significance_en, temple.significance_hi);
  const faith = FAITH_LABELS[temple.faith];

  const handleCardClick = () => {
    setOpen(o => !o);
    if (open) setStoryOpen(false);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const url = temple.id
      ? `${window.location.origin}${window.location.pathname}#${temple.id}`
      : window.location.href;
    const shareData = {
      title: temple.name_en,
      text: `${temple.name_en} · ${temple.city} — Divine South India`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2500);
        } catch {}
      }
    }
  };

  return (
    <article id={temple.id || undefined} className={`temple-card faith-${temple.faith}${open?' open':''}${isVisited?' is-visited':''}`} onClick={handleCardClick}>
      <div className="card-top">
        <div className="card-faith-dot" />
        <div className="card-names">
          <h3 className="card-name">{name}</h3>
          <p className="card-name-sub">{sub}</p>
        </div>
        {hasImg && !open && (
          <img className="card-thumb" src={temple.image_url} alt={name} loading="lazy" onError={() => setImgError(true)} />
        )}
        {temple.id && (
          <button
            className={`card-visited-btn${isVisited ? ' is-visited' : ''}`}
            onClick={e => { e.stopPropagation(); toggle(temple.id); }}
            title={isVisited ? L('Remove from visited', 'विज़िट हटाएं') : L('Mark as visited', 'विज़िट चिह्नित करें')}
          >
            {isVisited ? '✓' : '○'}
          </button>
        )}
        <span className="card-chevron">{open ? '▲' : '▼'}</span>
      </div>

      <div className="card-meta">
        <span className="card-city">📍 {temple.city}</span>
        {distance !== null && <span className="card-distance">{fmtKm(distance)}</span>}
        <span className="card-faith-label">{L(faith.en, faith.hi)}</span>
        {temple.alzhwar_star   && <span className="badge badge-alzhwar"><span style={{color:'#c0392b'}}>☸</span> {L('Āḻvārs','अल्झवार')}</span>}
        {temple.nayanmars_star && <span className="badge badge-nayanmars">🔱 {L('Nayanmars','नयनमार')}</span>}
        {hasNote && !open && <span className="card-note-indicator" title={L('Has a note', 'नोट है')}>📝</span>}
      </div>

      {open && (
        <div className="card-story fade-in" onClick={e => e.stopPropagation()}>
          {hasImg && (
            <img className="card-hero-img" src={temple.image_url} alt={name} loading="lazy" onError={() => setImgError(true)} />
          )}
          {deity && <p className="card-deity">{L('Deity:','देवता:')} <strong>{deity}</strong></p>}
          {temple.dynasty && <p className="card-dynasty">{L('Dynasty:','राजवंश:')} {temple.dynasty}{temple.century ? ` · ${temple.century}` : ''}</p>}
          <div className="card-divider" />
          {storyOpen && <p className="card-story-text fade-in">{story}</p>}
          {sig && (
            <div className="card-significance">
              <span className="sig-label">{L('Significance','महत्त्व')}</span>
              <p>{sig}</p>
            </div>
          )}
          {story && (
            <button className="card-story-toggle" onClick={() => setStoryOpen(o => !o)}>
              {storyOpen ? L('Hide story ↑', 'कहानी छुपाएं ↑') : L('Read full story →', 'पूरी कहानी पढ़ें →')}
            </button>
          )}
          {isVisited && temple.id && (
            <div className="card-note-section">
              <span className="card-note-label">📝 {L('Your note', 'आपकी टिप्पणी')}</span>
              <textarea
                className="card-note-input"
                placeholder={L('Write your memory, experience, or travel tip…', 'यात्रा की याद लिखें…')}
                defaultValue={notes[temple.id] || ''}
                onChange={e => setNote(temple.id, e.target.value)}
                rows={3}
              />
            </div>
          )}

          <button className={`card-share-btn${copied ? ' copied' : ''}`} onClick={handleShare}>
            {copied ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {L('Link copied!', 'लिंक कॉपी हुआ!')}
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
                </svg>
                {L('Share this temple', 'शेयर करें')}
              </>
            )}
          </button>
        </div>
      )}
    </article>
  );
}
